import { useState, useEffect } from 'react';
import API from '../utils/api';

const getVideoEmbedInfo = (url) => {
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  if (ytMatch) {
    return {
      type: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`,
      thumbnail: `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`,
    };
  }
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return {
      type: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      thumbnail: null,
    };
  }
  // Direct link
  return { type: 'direct', embedUrl: url, thumbnail: null };
};

const VideoCard = ({ video, onPlay, delay = 0 }) => {
  const info = getVideoEmbedInfo(video.url);
  const thumb = video.thumbnail || info.thumbnail;

  return (
    <div
      className="video-card reveal"
      style={{ transitionDelay: `${delay}ms` }}
      onClick={() => onPlay(video, info)}
    >
      <div className="video-card-thumb">
        {thumb
          ? <img src={thumb} alt={video.title} className="video-thumb-img" />
          : <div className="video-thumb-placeholder">▶</div>
        }
        <div className="video-play-overlay">
          <div className="video-play-btn">▶</div>
        </div>
      </div>
      <div className="video-card-body">
        <h3 className="video-card-title">{video.title}</h3>
        {video.description && <p className="video-card-desc">{video.description}</p>}
      </div>
    </div>
  );
};

const VideoModal = ({ video, info, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="video-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="video-modal">
        <button className="video-modal-close" onClick={onClose}>✕</button>
        <div className="video-embed-wrap">
          {info.type === 'youtube' || info.type === 'vimeo' ? (
            <iframe
              src={info.embedUrl + '?autoplay=1'}
              title={video.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="video-embed-frame"
            />
          ) : (
            <video controls autoPlay className="video-embed-frame">
              <source src={info.embedUrl} />
              Your browser does not support the video tag.
            </video>
          )}
        </div>
        <div className="video-modal-info">
          <h3 className="video-modal-title">{video.title}</h3>
          {video.description && <p className="video-modal-desc">{video.description}</p>}
        </div>
      </div>
    </div>
  );
};

const VideoSection = ({ page }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    setLoading(true);
    API.get(`/videos/page/${page}`)
      .then(r => { setVideos(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page]);

  if (loading) return null;
  if (videos.length === 0) return null;

  return (
    <section className="section section-sm videos-section">
      <div className="section-label">Watch & Learn</div>
      <h2 className="section-title">Videos</h2>
      <p className="section-desc">Curated video content to deepen your knowledge and inspire your journey.</p>

      <div className="videos-grid">
        {videos.map((video, i) => (
          <VideoCard
            key={video._id}
            video={video}
            delay={i * 80}
            onPlay={(v, info) => setPlaying({ video: v, info })}
          />
        ))}
      </div>

      {playing && (
        <VideoModal
          video={playing.video}
          info={playing.info}
          onClose={() => setPlaying(null)}
        />
      )}
    </section>
  );
};

export default VideoSection;
